import React, { useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Clock, Inbox, Calendar, User, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, startOfWeek, addDays, isToday, addWeeks, subWeeks, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { OrdenTrabajoEditForm } from './OrdenTrabajoEditForm';
import { BandejaOTs } from './BandejaOTs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Tecnico {
  user_id: string;
  nombre: string;
  avatar_url?: string | null;
}

import { OrdenTrabajo } from '@/contexts/OrdenesTrabajoContext';

interface VistaSemanalConBandejaProps {
  ordenes: OrdenTrabajo[];
  tecnicos: Tecnico[];
  selectedTecnicos?: string[];
  clientes: { id: string; nombre: string }[];
}

const prioridadColors: Record<string, string> = {
  baja: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
  media: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
  alta: 'bg-orange-500/20 border-orange-500/50 text-orange-400',
  urgente: 'bg-red-500/20 border-red-500/50 text-red-400',
};

const estadoColors: Record<string, string> = {
  creada: 'bg-slate-500',
  asignada: 'bg-blue-500',
  en_progreso: 'bg-amber-500',
  finalizada: 'bg-emerald-500',
  cancelada: 'bg-red-500',
};

const prioridadLabels: Record<string, string> = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

const prioridadOrder: Record<string, number> = {
  urgente: 0,
  alta: 1,
  media: 2,
  baja: 3,
};

export const VistaSemanalConBandeja: React.FC<VistaSemanalConBandejaProps> = ({ 
  ordenes, 
  tecnicos, 
  selectedTecnicos,
  clientes 
}) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedOrden, setSelectedOrden] = useState<OrdenTrabajo | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [bandejaSelectedIds, setBandejaSelectedIds] = useState<Set<string>>(new Set());
  
  const { updateOrden, refreshOrdenes, assignTecnico, assignMultipleToTecnico } = useOrdenesTrabajoContext();

  // Generate week days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  // Filter technicians based on selection
  const filteredTecnicos = useMemo(() => {
    if (!selectedTecnicos || selectedTecnicos.length === 0) {
      return tecnicos;
    }
    return tecnicos.filter(t => selectedTecnicos.includes(t.user_id));
  }, [tecnicos, selectedTecnicos]);

  // Orders in bandeja (unassigned, excluding finalized/cancelled)
  const ordenesEnBandeja = useMemo(() => {
    return ordenes
      .filter(o => !o.tecnico_asignado_id && !['finalizada', 'cancelada'].includes(o.estado))
      .sort((a, b) => {
        const prioridadDiff = prioridadOrder[a.prioridad] - prioridadOrder[b.prioridad];
        if (prioridadDiff !== 0) return prioridadDiff;
        if (a.fecha_limite && b.fecha_limite) {
          return new Date(a.fecha_limite).getTime() - new Date(b.fecha_limite).getTime();
        }
        if (a.fecha_limite) return -1;
        if (b.fecha_limite) return 1;
        return 0;
      });
  }, [ordenes]);

  // Organize orders by technician and date
  const ordenesByTecnicoAndDate = useMemo(() => {
    const map: Record<string, Record<string, OrdenTrabajo[]>> = {};
    
    // Initialize map for all technicians
    filteredTecnicos.forEach(tecnico => {
      map[tecnico.user_id] = {};
      weekDays.forEach(day => {
        map[tecnico.user_id][format(day, 'yyyy-MM-dd')] = [];
      });
    });

    // Filter and populate orders (only assigned ones)
    ordenes
      .filter(o => o.estado !== 'finalizada' && o.estado !== 'cancelada')
      .filter(o => o.tecnico_asignado_id) // Only assigned orders
      .filter(orden => {
        if (!selectedTecnicos || selectedTecnicos.length === 0) return true;
        return selectedTecnicos.includes(orden.tecnico_asignado_id!);
      })
      .forEach(orden => {
        const tecnicoId = orden.tecnico_asignado_id!;
        if (!map[tecnicoId]) return;
        
        if (orden.fecha_limite) {
          const fechaKey = format(new Date(orden.fecha_limite), 'yyyy-MM-dd');
          if (map[tecnicoId][fechaKey]) {
            map[tecnicoId][fechaKey].push(orden);
          }
        }
      });

    // Sort orders by time within each day
    Object.keys(map).forEach(tecnicoId => {
      Object.keys(map[tecnicoId]).forEach(dateKey => {
        map[tecnicoId][dateKey].sort((a, b) => {
          const timeA = a.hora_ejecucion || '23:59';
          const timeB = b.hora_ejecucion || '23:59';
          return timeA.localeCompare(timeB);
        });
      });
    });

    return map;
  }, [ordenes, filteredTecnicos, weekDays, selectedTecnicos]);

  const formatTime = (time: string | undefined | null) => {
    if (!time) return null;
    return time.slice(0, 5);
  };

  const getFechaLimiteStatus = (fechaLimite?: string | null) => {
    if (!fechaLimite) return null;
    const fecha = new Date(fechaLimite);
    if (isPast(fecha) && !isToday(fecha)) {
      return { text: 'Vencida', className: 'text-red-400' };
    }
    if (isToday(fecha)) {
      return { text: 'Hoy', className: 'text-yellow-400' };
    }
    return null;
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const ordenId = draggableId;
    const orden = ordenes.find(o => o.id === ordenId);
    if (!orden) return;

    // Handle dropping back to bandeja (unassign)
    if (destination.droppableId === 'bandeja') {
      try {
        await assignTecnico(ordenId, null);
        toast.success('Orden devuelta a la bandeja');
      } catch (error) {
        toast.error('Error al devolver la orden');
      }
      return;
    }

    // Parse destination: tecnicoId_date
    const [destTecnicoId, destDate] = destination.droppableId.split('_');
    const tecnicoNombre = tecnicos.find(t => t.user_id === destTecnicoId)?.nombre || 'técnico';
    
    // From bandeja to technician cell - check if multiple selected
    if (source.droppableId === 'bandeja') {
      // Check if we're dragging a selected item - if so, assign all selected
      if (bandejaSelectedIds.has(ordenId) && bandejaSelectedIds.size > 1) {
        try {
          // Assign all selected to the same technician and date
          const selectedArray = Array.from(bandejaSelectedIds);
          for (const id of selectedArray) {
            await updateOrden(id, {
              tecnico_asignado_id: destTecnicoId,
              fecha_limite: destDate,
              estado: 'asignada' as any,
            });
          }
          await refreshOrdenes();
          toast.success(`${bandejaSelectedIds.size} órdenes asignadas a ${tecnicoNombre} - ${format(new Date(destDate), 'dd/MM')}`);
          setBandejaSelectedIds(new Set());
        } catch (error) {
          toast.error('Error al asignar las órdenes');
        }
      } else {
        try {
          await updateOrden(ordenId, {
            tecnico_asignado_id: destTecnicoId,
            fecha_limite: destDate,
            estado: 'asignada' as any,
          });
          await refreshOrdenes();
          toast.success(`Orden asignada a ${tecnicoNombre} - ${format(new Date(destDate), 'dd/MM')}`);
        } catch (error) {
          toast.error('Error al asignar la orden');
        }
      }
      return;
    }

    // From technician cell to another technician cell
    const newTecnicoId = destTecnicoId;
    const currentFecha = orden.fecha_limite ? format(new Date(orden.fecha_limite), 'yyyy-MM-dd') : null;

    if (orden.tecnico_asignado_id !== newTecnicoId || currentFecha !== destDate) {
      try {
        await updateOrden(ordenId, {
          tecnico_asignado_id: newTecnicoId,
          fecha_limite: destDate,
          estado: newTecnicoId && orden.estado === 'creada' ? 'asignada' as any : orden.estado as any,
        });
        await refreshOrdenes();
        
        toast.success(`Orden movida a ${tecnicoNombre} - ${format(new Date(destDate), 'dd/MM')}`);
      } catch (error) {
        toast.error('Error al mover la orden');
      }
    }
  };

  const handleOpenEditForm = (orden: OrdenTrabajo) => {
    setSelectedOrden(orden);
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => {
    setSelectedOrden(null);
    setShowEditForm(false);
  };

  const handleEditSuccess = () => {
    // Local state already updated by context mutations (updateOrden, assignTecnicos).
    // No need to reload — that was triggering cascading re-renders.
  };

  const getInitials = (nombre: string) => {
    return nombre.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const goToPreviousWeek = () => setCurrentWeekStart(prev => subWeeks(prev, 1));
  const goToNextWeek = () => setCurrentWeekStart(prev => addWeeks(prev, 1));
  const goToCurrentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  return (
    <div className="space-y-4">
      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-card/50 p-3 rounded-lg border border-border/50">
        <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Anterior
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goToCurrentWeek}>
            <Calendar className="h-4 w-4 mr-2" />
            Hoy
          </Button>
          <span className="font-semibold text-foreground">
            {format(currentWeekStart, "d 'de' MMMM", { locale: es })} - {format(addDays(currentWeekStart, 6), "d 'de' MMMM yyyy", { locale: es })}
          </span>
        </div>

        <Button variant="outline" size="sm" onClick={goToNextWeek}>
          Siguiente
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Timeline Grid with Bandeja */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4">
          {/* Bandeja Column - Using shared component */}
          <BandejaOTs 
            ordenes={ordenes}
            tecnicos={tecnicos}
            onOrdenClick={handleOpenEditForm}
            selectedIds={bandejaSelectedIds}
            onSelectionChange={setBandejaSelectedIds}
          />

          {/* Week Grid */}
          <div className="flex-1 border border-border rounded-lg overflow-hidden bg-card/30">
            {/* Header Row - Days */}
            <div className="grid grid-cols-8 bg-muted/50 border-b border-border">
              <div className="p-3 border-r border-border">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <User className="h-4 w-4" />
                  Técnico
                </div>
              </div>
              {weekDays.map((day, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "p-2 text-center border-r border-border last:border-r-0",
                    isToday(day) && "bg-primary/10"
                  )}
                >
                  <div className={cn(
                    "text-xs uppercase tracking-wide",
                    isToday(day) ? "text-primary font-semibold" : "text-muted-foreground"
                  )}>
                    {format(day, 'EEE', { locale: es })}
                  </div>
                  <div className={cn(
                    "text-lg font-bold",
                    isToday(day) ? "text-primary" : "text-foreground"
                  )}>
                    {format(day, 'd')}
                  </div>
                </div>
              ))}
            </div>

            {/* Technician Rows */}
            <ScrollArea className="max-h-[500px]">
              {filteredTecnicos.map((tecnico, tecIndex) => (
                <div 
                  key={tecnico.user_id} 
                  className={cn(
                    "grid grid-cols-8 border-b border-border last:border-b-0",
                    tecIndex % 2 === 0 ? "bg-background/50" : "bg-muted/20"
                  )}
                >
                  {/* Technician Name Cell */}
                  <div className="p-3 border-r border-border flex items-center gap-2 min-h-[80px]">
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarImage src={tecnico.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                        {getInitials(tecnico.nombre)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium truncate text-foreground">
                      {tecnico.nombre}
                    </span>
                  </div>

                  {/* Day Cells */}
                  {weekDays.map((day) => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const droppableId = `${tecnico.user_id}_${dateKey}`;
                    const dayOrdenes = ordenesByTecnicoAndDate[tecnico.user_id]?.[dateKey] || [];

                    return (
                      <Droppable key={droppableId} droppableId={droppableId}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={cn(
                              "p-1 border-r border-border last:border-r-0 min-h-[80px] transition-all duration-200",
                              isToday(day) && "bg-primary/5",
                              snapshot.isDraggingOver && "bg-emerald-500/20 ring-2 ring-inset ring-emerald-500/50 scale-[1.02]"
                            )}
                          >
                            <div className="space-y-1">
                              {dayOrdenes.map((orden, index) => (
                                                <Draggable 
                                                  key={orden.id} 
                                                  draggableId={orden.id} 
                                                  index={index}
                                                >
                                                  {(provided, snapshot) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      onClick={() => handleOpenEditForm(orden)}
                                                      className={cn(
                                                        "p-1.5 rounded border text-xs cursor-grab active:cursor-grabbing transition-shadow",
                                                        prioridadColors[orden.prioridad],
                                                        snapshot.isDragging && "shadow-2xl ring-2 ring-primary z-[9999]"
                                                      )}
                                                    >
                                      <div className="flex items-center gap-1 mb-0.5">
                                        <div className={cn(
                                          "w-1.5 h-1.5 rounded-full flex-shrink-0",
                                          estadoColors[orden.estado]
                                        )} />
                                        {orden.hora_ejecucion && (
                                          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-primary">
                                            <Clock className="h-2.5 w-2.5" />
                                            {formatTime(orden.hora_ejecucion)}
                                          </span>
                                        )}
                                        <span className="font-mono text-[10px] opacity-70 ml-auto">
                                          {orden.numero_ot}
                                        </span>
                                      </div>
                                      <div className="font-medium truncate leading-tight">
                                        {orden.titulo}
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                            </div>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    );
                  })}
                </div>
              ))}
            </ScrollArea>
          </div>
        </div>
      </DragDropContext>

      {/* Edit Form Dialog */}
      {selectedOrden && (
        <OrdenTrabajoEditForm
          orden={selectedOrden as any}
          isOpen={showEditForm}
          onClose={handleCloseEditForm}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};
